import { Outlet, Link, useNavigate } from 'react-router';
import { useState, useEffect, useCallback } from 'react';
import { Loader2, LogOut, Menu, X, Search, ChevronDown, PanelLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useEnvironment, ENV_COLORS } from '../context/EnvironmentContext';
import type { ValidEnvironment } from '../context/EnvironmentContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { Sidebar } from '../components/Sidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SearchOverlay, useSearchHotkey } from '../components/search/SearchOverlay';
import { OnboardingTour, useOnboardingTour } from '../components/OnboardingTour';
import { networkManager } from '../api/NetworkManager';
import { useQueryClient } from '@tanstack/react-query';

function EnvironmentSwitcher() {
  const { environment, setEnvironment } = useEnvironment();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmProd, setConfirmProd] = useState(false);
  const colors = ENV_COLORS[environment];

  useEffect(() => {
    networkManager.setEnvironment(environment);
  }, [environment]);

  function handleSelect(env: ValidEnvironment) {
    if (env === 'prod' && environment !== 'prod') {
      setConfirmProd(true);
      return;
    }
    setEnvironment(env);
    queryClient.invalidateQueries();
    setOpen(false);
    setConfirmProd(false);
  }

  function confirmProduction() {
    setEnvironment('prod');
    queryClient.invalidateQueries();
    setOpen(false);
    setConfirmProd(false);
  }

  return (
    <div className="relative">
      <button
        onClick={() => {
          setOpen(!open);
          setConfirmProd(false);
        }}
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors hover:bg-surface-sunken"
      >
        <span className={`h-2 w-2 rounded-full ${colors.dot}`} />
        <span className={colors.text}>{colors.label}</span>
        <ChevronDown className="h-3 w-3 text-ink-tertiary" />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setOpen(false);
              setConfirmProd(false);
            }}
          />
          <div className="absolute right-0 top-full mt-1 w-48 rounded-xl border border-ring bg-surface-raised p-1 shadow-float animate-fade-in z-50">
            {confirmProd ? (
              <div className="p-3">
                <p className="text-xs font-medium text-status-error">Switch to Production?</p>
                <p className="mt-1 text-2xs text-ink-secondary">
                  All actions will affect live data.
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setConfirmProd(false)}
                    className="btn-ghost text-xs flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmProduction}
                    className="bg-status-error text-white rounded-lg px-3 py-1.5 text-xs font-medium flex-1"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            ) : (
              (['dev', 'staging', 'prod'] as ValidEnvironment[]).map((env) => {
                const c = ENV_COLORS[env];
                const isActive = env === environment;
                return (
                  <button
                    key={env}
                    onClick={() => handleSelect(env)}
                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? 'bg-surface-sunken font-medium' : 'hover:bg-surface-sunken'}`}
                  >
                    <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                    <span className={c.text}>{c.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const displayName = user?.displayName ?? user?.email?.split('@')[0] ?? user?.username ?? 'User';
  const initials =
    user?.firstName && user?.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
      : displayName.replace(/^Yum_/i, '').slice(0, 2).toUpperCase();

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-xs font-bold text-ink-inverse shrink-0">
          {initials}
        </div>
        <div className="hidden lg:block">
          <p className="text-sm font-medium text-ink leading-tight">{displayName}</p>
          <p className="text-2xs text-ink-tertiary">{user?.roles?.[0] ?? 'user'}</p>
        </div>
      </div>
      <button onClick={logout} className="btn-ghost" title="Sign out">
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}

const SIDEBAR_COLLAPSED_KEY = 'byte-sidebar-collapsed';

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function DashboardLayout() {
  const { isAuthenticated, isLoading, login, isEmulating, user, stopEmulation } = useAuth();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const { run: tourRun, restart: restartTour, handleEvent: tourEvent } = useOnboardingTour();

  useSearchHotkey(setSearchOpen);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 bg-surface">
        <img src="/byte-lockup.png" alt="Byte by Yum!" className="h-12 w-auto" />
        <p className="font-display text-lg font-bold tracking-tight text-ink-strong">Coach CMDB</p>
        <p className="-mt-3 text-ink-secondary">Support platform for Byte Coach</p>
        <button onClick={login} className="btn-primary">
          Sign in with SSO
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* Top bar — full width across the viewport */}
      <header className="sticky top-0 z-40 border-b border-ring bg-surface-raised/80 backdrop-blur-sm">
        <div className="flex h-14 items-center gap-3 px-4 sm:px-6">
          {isMobile ? (
            <>
              <button
                onClick={() => setSidebarOpen(true)}
                className="btn-ghost -ml-2"
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" />
              </button>
              <Link to="/" className="flex items-center" title="Byte Coach CMDB">
                <img src="/byte-lockup.png" alt="Byte by Yum!" className="h-6 w-auto" />
              </Link>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/" className="flex items-center gap-2.5" title="Byte Coach CMDB">
                <img src="/byte-lockup.png" alt="Byte by Yum!" className="h-6 w-auto" />
                <span className="hidden lg:inline text-[11px] font-bold uppercase tracking-[0.16em] text-ink-tertiary">
                  Coach CMDB
                </span>
              </Link>
              <button
                onClick={toggleSidebarCollapsed}
                className="btn-ghost"
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <PanelLeft className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Search bar (desktop) */}
          <div className="flex-1 max-w-md hidden md:block" data-tour="search-bar">
            <button
              onClick={() => setSearchOpen(true)}
              className="flex w-full items-center gap-2 rounded-xl border border-ring bg-surface px-3.5 py-2 text-sm text-ink-tertiary hover:border-ink-tertiary transition-colors"
            >
              <Search className="h-4 w-4" />
              <span className="flex-1 text-left">Search or press</span>
              <kbd className="rounded bg-surface-sunken px-1.5 py-0.5 text-2xs font-mono">⌘K</kbd>
            </button>
          </div>

          <div className="flex flex-1 md:flex-none items-center justify-end gap-2">
            {isMobile && (
              <button onClick={() => navigate('/search')} className="btn-ghost" aria-label="Search">
                <Search className="h-5 w-5" />
              </button>
            )}
            <span data-tour="env-switcher">
              <EnvironmentSwitcher />
            </span>
            <div className="h-6 w-px bg-ring hidden sm:block" />
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Sidebar + content row, beneath the full-width bar */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar — desktop */}
        {!isMobile && (
          <aside
            data-tour="sidebar-nav"
            className={`sticky top-14 h-[calc(100vh-3.5rem)] shrink-0 overflow-y-auto border-r border-white/10 bg-cosmos-flat scrollbar-hide transition-[width] duration-200 ${
              sidebarCollapsed ? 'w-16' : 'w-64'
            }`}
          >
            <Sidebar collapsed={sidebarCollapsed} restartTour={restartTour} showBrand={false} />
          </aside>
        )}

        {/* Sidebar — mobile overlay */}
        {isMobile && sidebarOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
              onClick={closeSidebar}
            />
            <aside className="fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto bg-cosmos-flat shadow-float animate-slide-in-right">
              <button
                onClick={closeSidebar}
                className="absolute right-3 top-4 z-10 p-1 text-sidebar-text-muted hover:text-sidebar-text-active"
                aria-label="Close navigation"
              >
                <X className="h-5 w-5" />
              </button>
              <Sidebar onNavigate={closeSidebar} />
            </aside>
          </>
        )}

        {/* Main column */}
        <div className="flex flex-1 flex-col min-w-0">
          {/* Emulation banner */}
          {isEmulating && user?.emulating && (
            <div className="bg-status-warning-bg text-[#7A4B07] border-b border-status-warning/40 px-4 py-2 flex items-center justify-between text-sm font-medium">
              <span>
                Emulating <strong>{user.emulating.targetYumId}</strong> — you are seeing the
                platform as they would
              </span>
              <button
                onClick={stopEmulation}
                className="bg-status-warning/20 hover:bg-status-warning/30 rounded px-3 py-1 text-xs font-bold transition-colors"
              >
                Stop Emulating
              </button>
            </div>
          )}

          {/* Main content */}
          <main className="flex-1 min-w-0 px-4 sm:px-6 py-6">
            <Breadcrumbs />
            <Outlet />
          </main>
        </div>
      </div>

      {/* Search overlay */}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Onboarding tour */}
      <OnboardingTour run={tourRun} onEvent={tourEvent} />
    </div>
  );
}
